import { BrowserClient, feedbackIntegration, getClient } from '@sentry/react';
import { useColorScheme } from '@sixui/ui';
import { useTranslation } from 'react-i18next';

import { getAppSettings } from '~/lib/appSettings';

export type IUseFeedbackDialogResult = {
  enabled: boolean;
  open: () => Promise<void>;
};

export const useFeedbackDialog = (): IUseFeedbackDialogResult => {
  const { t } = useTranslation();
  const sentryClient = getClient<BrowserClient>();
  const colorScheme = useColorScheme();
  const sentryFeedback =
    sentryClient?.getIntegrationByName<ReturnType<typeof feedbackIntegration>>(
      'Feedback',
    );
  const appSettings = getAppSettings();
  const enabled = !!appSettings.analytics.sentry?.enabled;

  const handleOpen = async (): Promise<void> => {
    if (!sentryFeedback) {
      return;
    }

    const form = await sentryFeedback.createForm({
      onFormClose: () => {
        form.removeFromDom();
        form.close();
      },
      onSubmitSuccess: () => {
        form.close();
      },
      onSubmitError: () => {
        form.close();
      },
      onFormSubmitted: () => {
        form.removeFromDom();
        form.close();
      },
      formTitle: t('SentryFeedbackForm.formTitle'),
      submitButtonLabel: t('SentryFeedbackForm.submitButtonLabel'),
      cancelButtonLabel: t('SentryFeedbackForm.cancelButtonLabel'),
      confirmButtonLabel: t('SentryFeedbackForm.confirmButtonLabel'),
      addScreenshotButtonLabel: t(
        'SentryFeedbackForm.addScreenshotButtonLabel',
      ),
      removeScreenshotButtonLabel: t(
        'SentryFeedbackForm.removeScreenshotButtonLabel',
      ),
      nameLabel: t('SentryFeedbackForm.nameLabel'),
      namePlaceholder: t('SentryFeedbackForm.namePlaceholder'),
      emailLabel: t('SentryFeedbackForm.emailLabel'),
      emailPlaceholder: t('SentryFeedbackForm.emailPlaceholder'),
      isRequiredLabel: t('SentryFeedbackForm.isRequiredLabel'),
      messageLabel: t('SentryFeedbackForm.messageLabel'),
      messagePlaceholder: t('SentryFeedbackForm.messagePlaceholder'),
      successMessageText: t('SentryFeedbackForm.successMessageText'),
      useSentryUser: {
        name: 'fullName',
        email: 'email',
      },
      colorScheme: colorScheme.variant,
      showBranding: false,
      themeLight: {
        outline: '0',
      },
      themeDark: {
        outline: '0',
      },
    });
    form.appendToDom();
    form.open();
  };

  return { enabled, open: handleOpen };
};
